import { ChangeEvent, ReactNode, useEffect, useState } from "react"
import { InputText } from "primereact/inputtext"
import { InputNumber, InputNumberChangeParams } from "primereact/inputnumber"
import { Checkbox, CheckboxChangeParams } from "primereact/checkbox"
import { Button } from "primereact/button"
import { Fieldset } from "primereact/fieldset"
import commonStyles from "../../common.module.css"
import OrderFormGoodsItemADR from "./GoodsItemADR"
import { GetOrderGoodsPackingsResponseData, GoodsPacking, OrderGoods, OrderGoodsADR } from "../../../api/types/order"
import FieldError, { FormErrors, FormErrorsArr } from "../../FieldError"
import { classNames } from "primereact/utils"
import { Dropdown, DropdownChangeParams, DropdownProps } from "primereact/dropdown"
import { useGoodsPackings } from "../../../api/queries/useGoodsPackings"
import { useParams } from "react-router-dom"
import { OrderParams } from "."
import { useOrderDetail } from "../../../api/queries/useOrderDetail"
import styles from "./styles.module.css"

export default function OrderFormGoodsItem({
  index,
  item,
  goodsItemChangeHandler,
  goodsItemRemoveHandler,
  goodsADRItemRemoveHandler,
  canRemove,
  errors,
  ADRErrors = [],
  isLast,
}: Props) {
  const { uuid } = useParams<OrderParams>()
  const { data: orderDetail } = useOrderDetail(uuid)
  const { data: packings }: { data: GetOrderGoodsPackingsResponseData | undefined } =
    useGoodsPackings({
      loadingCountryCode: orderDetail?.results.orderDetail.loading?.country,
      unloadingCountryCode: orderDetail?.results.orderDetail.unloading?.country,
    })

  const [volume, setVolume] = useState<string>("")
  const [isADRActive, setIsADRActive] = useState<boolean>(false)

  const handleContentChange = (event: ChangeEvent<HTMLInputElement>): void => {
    goodsItemChangeHandler(Object.assign({}, item, { content: event.target.value }), index)
  }

  const handlePiecesChange = (event: InputNumberChangeParams): void => {
    goodsItemChangeHandler(Object.assign({}, item, { pieces: event.value }), index)
  }

  const handleSignificationChange = (event: ChangeEvent<HTMLInputElement>): void => {
    goodsItemChangeHandler(Object.assign({}, item, { signification: event.target.value }), index)
  }

  const handleWeightChange = (event: InputNumberChangeParams): void => {
    goodsItemChangeHandler(Object.assign({}, item, { weight: event.value }), index)
  }

  const tPackingItem = (option: GoodsPacking): ReactNode => (
    `${option.value} - ${option.label}`
  )

  const tPackingValue = (option: GoodsPacking, props: DropdownProps): ReactNode => (
    option
      ? `${option.value} - ${option.label}`
      : `${props.placeholder || "empty"}`
  )
  const handlePackingChange = (event: DropdownChangeParams): void => {
    goodsItemChangeHandler(Object.assign({}, item, { packing: event.value }), index)
  }

  const handleLengthChange = (event: InputNumberChangeParams): void => {
    goodsItemChangeHandler(Object.assign({}, item, { length: event.value }), index)
  }

  const handleWidthChange = (event: InputNumberChangeParams): void => {
    goodsItemChangeHandler(Object.assign({}, item, { width: event.value }), index)
  }

  const handleHeightChange = (event: InputNumberChangeParams): void => {
    goodsItemChangeHandler(Object.assign({}, item, { height: event.value }), index)
  }

  const handleIsADRActiveChange = (event: CheckboxChangeParams): void => {
    if (event.checked) {
      goodsItemChangeHandler(Object.assign({}, item, { adr: [Object.assign({}, defaultEmptyADR)] }), index)
      setIsADRActive(true)
    } else {
      goodsItemChangeHandler(Object.assign({}, item, { adr: [] }), index)
      setIsADRActive(false)
    }
  }

  const handleAddADRClick = (): void => {
    let newADR = [...item.adr]
    newADR.push(Object.assign({}, defaultEmptyADR))
    goodsItemChangeHandler(Object.assign({}, item, { adr: newADR }), index)
  }

  const handleADRChange = (ADRItem: OrderGoodsADR, ADRindex: number): void => {
    let newADR = [...item.adr]
    newADR[ADRindex] = ADRItem
    goodsItemChangeHandler(Object.assign({}, item, { adr: newADR }), index)
  }

  const handleADRRemove = (ADRIndex: number): void => {
    goodsADRItemRemoveHandler(index, ADRIndex)
  }

  useEffect(() => {
    const vol = item.length * item.width * item.height * item.pieces / 1_000_000
    setVolume(vol.toFixed(3))
  }, [item.length, item.width, item.height, item.pieces])

  useEffect(() => {
    if (item && item.adr && item.adr.length > 0 && !isADRActive) {
      setIsADRActive(true)
    }
  }, [item, isADRActive])

  return (
    <>
      <div className={"block-title block-title_blue px-3 border-round-top flex align-items-center"}>
        Položka č. {index + 1}

        {canRemove && (
          <Button icon={"pi pi-times"} type={"button"}
            className={"p-button-text p-button-icon-only p-button-danger ml-auto"}
            onClick={() => goodsItemRemoveHandler(index)}
          />
        )}
      </div>

      <div className={`border-solid border-1 border-300 px-3 pt-5 pb-3 ${isLast ? "" : "mb-6"}`}>
        <div className={"grid"}>
          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputText value={item.content} onChange={handleContentChange}
                className={classNames({ "p-invalid": !!errors?.content })} />
              <label className={commonStyles.labelRequired}>Název zboží:</label>
            </span>
            <FieldError error={errors?.content} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputNumber value={item.pieces || null} onChange={handlePiecesChange} min={1}
                className={classNames({ "p-invalid": !!errors?.pieces })} />
              <label className={commonStyles.labelRequired}>Počet kusů:</label>
            </span>
            <FieldError error={errors?.pieces} />
            <small className={commonStyles.fieldHelp}>Jedná se např. o počet palet, nikoli kusů umístěných na jedné paletě.</small>
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputText value={item.signification || ""} onChange={handleSignificationChange}
                className={classNames({ "p-invalid": !!errors?.signification })} />
              <label>Označení:</label>
            </span>
            <FieldError error={errors?.signification} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputNumber value={item.weight || null} onChange={handleWeightChange} min={1}
                className={classNames({ "p-invalid": !!errors?.weight })} />
              <label className={commonStyles.labelRequired}>Hmotnost (kg):</label>
            </span>
            <FieldError error={errors?.weight} />
            <small className={commonStyles.fieldHelp}>Jedná se o hmotnost všech kusů včetně palety (obalu) této položky.</small>
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <Dropdown options={packings?.results.packings}
                value={item.packing} onChange={handlePackingChange}
                itemTemplate={tPackingItem} valueTemplate={tPackingValue}
                className={classNames({ "p-invalid": !!errors?.packing })} />
              <label className={commonStyles.labelRequired}>Obal:</label>
            </span>
            <FieldError error={errors?.packing} />
          </div>
        </div>

        <div className={"grid"}>
          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputNumber value={item.length || null} onChange={handleLengthChange} min={1}
                className={classNames({ "p-invalid": !!errors?.length })} />
              <label className={commonStyles.labelRequired}>Délka (cm):</label>
            </span>
            <FieldError error={errors?.length} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputNumber value={item.width || null} onChange={handleWidthChange} min={1}
                className={classNames({ "p-invalid": !!errors?.width })} />
              <label className={commonStyles.labelRequired}>Šířka (cm):</label>
            </span>
            <FieldError error={errors?.width} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputNumber value={item.height || null} onChange={handleHeightChange} min={1}
                className={classNames({ "p-invalid": !!errors?.height })} />
              <label className={commonStyles.labelRequired}>Výška (cm):</label>
            </span>
            <FieldError error={errors?.height} />
          </div>

          <div className={"field col-12 md:col-4"}>
            <span className={"p-float-label"}>
              <InputText disabled={true} value={volume} />
              <label>Objem (cbm - m<sup>3</sup>):</label>
            </span>
          </div>
        </div>

        <Fieldset className={"p-0"}>
          <div className={isADRActive ? "pb-3" : ""}>
            <Checkbox name={`adr-${index}`} inputId={`adr-${index}`} checked={isADRActive} onChange={handleIsADRActiveChange} />
            <label htmlFor={`adr-${index}`} className={"ml-2"}>ADR</label>
          </div>

          {isADRActive && (
            <div>
              {item.adr.map((adr: OrderGoodsADR, ADRIndex: number) => (
                <OrderFormGoodsItemADR
                  key={ADRIndex}
                  item={adr}
                  index={ADRIndex}
                  ADRChangeHandler={handleADRChange}
                  ADRRemoveHandler={handleADRRemove}
                  errors={ADRErrors[ADRIndex]}
                  canRemove={item.adr.length > 1}
                  isLast={ADRIndex === item.adr.length - 1}
                />
              ))}

              <div className={"text-center mb-6"}>
                {item.adr.length < MAX_ADR_COUNT && (
                  <Button type={"button"} icon={"pi pi-plus"}
                    className={`p-button-lg p-button-icon-only ${styles.addNewButtonGreen}`}
                    onClick={handleAddADRClick} title={"Přidat ADR"} />
                )}
              </div>
            </div>
          )}
        </Fieldset>
      </div>
    </>
  )
}

interface Props {
  index: number;
  item: OrderGoods;
  goodsItemChangeHandler: (good: OrderGoods, index: number) => void;
  goodsItemRemoveHandler: (index: number) => void;
  goodsADRItemRemoveHandler: (itemIndex: number, ADRIndex: number) => void;
  canRemove: boolean;
  errors: FormErrors<OrderGoods>;
  ADRErrors: FormErrorsArr<OrderGoodsADR>;
  isLast: boolean;
}

const defaultEmptyADR: OrderGoodsADR = {
  codeUN: 0,
  idAdr: 0,
  pieces: 0,
  brutto: 0,
  netto: 0,
  technicalName: "",
  technicalNameEN: "",
  description: "",
}

export const MAX_ADR_COUNT = 20;
